import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import {
  DEFAULT_WORKSPACE_BASE_COLOR_KEY,
  resolveWorkspacePalette,
} from './workspaceBaseColors';

const STATIC_PATH_PREFIXES = new Set([
  'home',
  'sys',
  'admin',
  'cg',
  'widget',
  'open',
  'test',
]);

export const useWorkspaceTheme = (): void => {
  const location = useLocation();
  const { workspaces } = useWorkspaceContext();

  const urlWorkspaceId = useMemo(() => {
    const firstSegment = location.pathname.split('/').filter(Boolean)[0];
    if (!firstSegment || STATIC_PATH_PREFIXES.has(firstSegment)) {
      return null;
    }
    return firstSegment;
  }, [location.pathname]);

  const activeWorkspace = useMemo(() => {
    if (urlWorkspaceId) {
      const byUrl = workspaces.find((workspace) => workspace.id === urlWorkspaceId);
      if (byUrl) return byUrl;
    }
    return null;
  }, [workspaces, urlWorkspaceId]);

  useEffect(() => {
    const palette = resolveWorkspacePalette(
      activeWorkspace?.baseColorKey || DEFAULT_WORKSPACE_BASE_COLOR_KEY
    );
    const root = document.documentElement;

    root.style.setProperty('--workspace-primary', palette.primary);
    root.style.setProperty('--workspace-primary-dark', palette.primaryDark);
    root.style.setProperty('--workspace-bg-soft', palette.bgSoft);
    root.style.setProperty('--workspace-border-soft', palette.borderSoft);
    root.style.setProperty('--workspace-text-on-primary', palette.textOnPrimary);
    root.style.setProperty('--workspace-suggest-token-bg', palette.bgSoft);

    root.style.setProperty('--beige-primary', palette.primary);
    root.style.setProperty('--beige-primary-dark', palette.primaryDark);
    root.style.setProperty('--beige-soft-bg', palette.bgSoft);
  }, [activeWorkspace?.id, activeWorkspace?.baseColorKey]);
};
